import { NgClass } from '@angular/common';
import { Component, computed, inject, input } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { TDeliverableForm } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/deliverable-list.interface';
import { ICtaAnalyticsData } from '@cta-analytics-dialog/cta-analytics-dialog-content/cta-analytics-table/cta-analytics-table.interface';
import { EProcessStatus } from '@enums/campaign-analytics.enum';
import { EDeliverableType, EPlatformFeature } from '@enums/campaign.enum';
import {
  EDetailLevel,
  ICampaignExportApi,
} from '@pages/campaigns/campaign-details/campaign-details-banner/campaign-export-dialog/campaign-export-dialog.interface';
import { SocialUtilitiesService } from '@utilities/social-utilities.service';
import { IDeliverableCard } from '../cta-analytics-dialog-content/cta-analytics-table/cta-analytics-details/cta-analytics-details-deliverable-card/cta-analytics-details-deliverable-card.interface';
import { IManualDeliverableStatusConfig } from '../cta-analytics-dialog-content/cta-analytics-table/cta-analytics-details/cta-analytics-details-deliverable-card/manual-deliverable-status/manual-devlierable-status.interface';
import { CtaAnalyticsDetailsStore } from '../cta-analytics-dialog-content/cta-analytics-table/cta-analytics-details/cta-analytics-details.store';
import { FacebookPostDetailsComponent } from './platforms/facebook-post-details/facebook-post-details.component';
import { InstagramPostDetailsComponent } from './platforms/instagram-post-details/instagram-post-details.component';
import { InstagramReelDetailsComponent } from './platforms/instagram-reel-details/instagram-reel-details.component';
import { InstagramStoryDetailsComponent } from './platforms/instagram-story-details/instagram-story-details.component';
import { TiktokPostDetailsComponent } from './platforms/tiktok-post-details/tiktok-post-details.component';
import { TwitchStreamDetailsComponent } from './platforms/twitch-stream-details/twitch-stream-details.component';
import { XPostDetailsComponent } from './platforms/x-post-details/x-post-details.component';
import { YoutubeShortDetailsComponent } from './platforms/youtube-short-details/youtube-short-details.component';
import { YoutubeStreamDetailsComponent } from './platforms/youtube-stream-details/youtube-stream-details.component';
import { YoutubeVodDetailsComponent } from './platforms/youtube-vod-details/youtube-vod-details.component';

@Component({
  imports: [
    NgClass,
    TwitchStreamDetailsComponent,
    InstagramPostDetailsComponent,
    InstagramReelDetailsComponent,
    YoutubeShortDetailsComponent,
    YoutubeVodDetailsComponent,
    FacebookPostDetailsComponent,
    TiktokPostDetailsComponent,
    XPostDetailsComponent,
    InstagramStoryDetailsComponent,
    YoutubeStreamDetailsComponent,
  ],
  selector: 'app-deliverable-analytics-content',
  styleUrls: ['./deliverable-analytics-content.component.scss'],
  templateUrl: './deliverable-analytics-content.component.html',
})
export class DeliverableAnalyticsContentComponent {
  public readonly deliverableForm = input.required<FormGroup<TDeliverableForm>>();
  public readonly deliverableId = input.required<string>();
  public readonly deliverableType = input.required<EDeliverableType>();
  public readonly executionAnalytics = input.required<ICtaAnalyticsData | null>();
  public readonly platformFeature = input.required<EPlatformFeature>();
  public readonly deliverableCardData = input.required<IDeliverableCard>();
  public readonly creatorBriefingMode = input.required<boolean>();
  public readonly exportMode = input.required<boolean>();
  public readonly campaignExport = input.required<ICampaignExportApi | null>();
  public readonly adminMode = input.required<boolean>();

  public readonly EPlatformFeature = EPlatformFeature;

  public readonly platform = computed(() => SocialUtilitiesService.getPlatform(this.platformFeature()));

  public readonly canClickOnDeliverable = computed(() => {
    return (
      !this.creatorBriefingMode() &&
      (!this.exportMode() ||
        (this.campaignExport()?.showDeliverableStatus &&
          this.campaignExport()?.detailLevel === EDetailLevel.detailedAnalytics))
    );
  });

  public readonly canChangeDeliverableStatus = computed(() => {
    return (
      !!this.canClickOnDeliverable() &&
      this.deliverableCardData().campaignProcessStatus === EProcessStatus.processing &&
      this.deliverableCardData().type !== EDeliverableType.audioTracking &&
      this.deliverableCardData().type !== EDeliverableType.textTracking
    );
  });

  public readonly manualDeliverableStatusConfig = computed<IManualDeliverableStatusConfig>(() => {
    const manualDeliverableStatuses = this.ctaAnalyticsDetailsStore.manualDeliverableStatuses$();

    return {
      actionsVisible: this.canChangeDeliverableStatus(),
      analyticsVisible:
        !this.creatorBriefingMode() &&
        (!this.exportMode() ||
          (!!this.campaignExport()?.showDeliverableStatus &&
            this.campaignExport()?.detailLevel === EDetailLevel.detailedAnalytics)),
      campaignExportId: this.exportMode() ? (this.campaignExport()?.id ?? null) : null,
      creatorBriefingMode: this.creatorBriefingMode(),
      deliverableCard: this.deliverableCardData(),
      exportMode: this.exportMode(),
      manualDeliverableStatus: manualDeliverableStatuses
        ? (manualDeliverableStatuses.get(this.deliverableId()) ?? null)
        : undefined,
      statusVisible:
        !this.exportMode() ||
        (!!this.campaignExport()?.showDeliverableStatus &&
          this.campaignExport()?.detailLevel !== EDetailLevel.generalInfo),
    } satisfies IManualDeliverableStatusConfig;
  });

  private readonly ctaAnalyticsDetailsStore = inject(CtaAnalyticsDetailsStore);
}
